import { concat } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import didIntersect from 'ember-scroll-modifiers/modifiers/did-intersect';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import safeFormatTime from '../../helpers/safe-format-time.ts';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Modal from 'tio-ui/components/modal';
import Table from 'tio-ui/components/table';
import Paginator from 'tio-ui/components/paginator';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { Store } from '@warp-drive/core';
import type { NotificationHistoryExtension as NotificationHistoryModel } from 'tio-data/data/resources/notification-history.ext';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import { SkipErrorReporting } from 'tio-data/request-options';

interface NotificationHistoriesTableSignature {
  Args: {
    employee: EmployeeModel;
    noteSubtypeFilter: string;
    limit: number;
  };
}

export default class NotificationHistoriesTableComponent extends Component<NotificationHistoriesTableSignature> {
  @service declare store: Store;
  @tracked page = 1;
  @tracked isInViewport = false;
  // The row whose sent-content dialog is open (tio-api#10484 / fe#10443).
  @tracked snapshotRow: NotificationHistoryModel | null = null;

  get isLoading() {
    return this.notificationHistoriesData.isLoading;
  }

  notificationHistoriesData = trackedFunction(this, async () => {
    if (!this.args.employee) {
      return { data: [] as NotificationHistoryModel[], meta: undefined };
    }
    const limit = this.args.limit || 10;
    const offset = (this.page - 1) * limit;

    const params: Record<string, string | number | boolean> = {
      'filter[employee]': this.args.employee.id as string,
      'page[limit]': limit,
      'page[offset]': offset,
      sort: '-created_at',
    };

    if (this.args.noteSubtypeFilter) {
      params['filter[noteSubtype]'] = this.args.noteSubtypeFilter;
    }

    const { content } = (await this.store.request(
      query<NotificationHistoryModel>('notification-history', params)
    )) as {
      content: { data: NotificationHistoryModel[]; meta?: { page?: { total?: number } } };
    };
    return { data: content.data, meta: content.meta };
  });

  get notificationHistories() {
    return this.notificationHistoriesData?.value?.data || [];
  }

  // The sent content is deliberately absent from the list payload (the API
  // serves sentSubject/sentBodyHtml on show only, to admin requesters), so
  // the dialog fetches its row individually on open.
  snapshotData = trackedFunction(this, async () => {
    const row = this.snapshotRow;
    if (!row?.hasSentSnapshot) return null;

    // reload: the list payload deliberately omits sentBodyHtml (~50KB, served
    // on show only), so the cached record from the table query never carries
    // it — without a forced reload findRecord returns that bodyless copy and
    // the dialog spins forever.
    // SkipErrorReporting + a null fallback: a forbidden/failed show is an
    // expected outcome here (the endpoint's own auth), so it settles into the
    // dialog's unavailable state rather than throwing or firing a global
    // error toast.
    try {
      const { content } = (await this.store.request({
        ...findRecord<NotificationHistoryModel>('notification-history', row.id as string, {
          reload: true,
        }),
        options: { [SkipErrorReporting]: true },
      })) as { content: { data: NotificationHistoryModel } };
      return content.data;
    } catch {
      return null;
    }
  });

  get snapshot() {
    return this.snapshotData?.value ?? null;
  }

  // `sandbox=""` stops scripts and same-origin access but NOT subresource
  // fetches, and a delivered email is full of them — including the compliance
  // footer's tracking pixel, which the snapshot captures already resolved. So
  // merely OPENING this preview would record an "open" against the recipient,
  // corrupting the very history the admin is reading, and leak the admin's
  // network metadata to every host the email references.
  // The CSP closes that: nothing loads over the network, `data:` images still
  // paint, and `unsafe-inline` styles keep the email looking like itself (email
  // HTML is inline styles plus one `<style>` block by construction).
  get sandboxedSnapshotHtml() {
    const html = this.snapshot?.sentBodyHtml;
    if (!html) return null;

    const meta =
      '<meta http-equiv="Content-Security-Policy" ' +
      `content="default-src 'none'; style-src 'unsafe-inline'; img-src data:">`;

    // A meta policy governs only what follows it, so it goes FIRST inside
    // <head>. The lookahead makes the tag name END there: a bare `<head` also
    // matches `<header ...>`, and a header in the BODY would then swallow the
    // policy, where a meta CSP is not honored — silently dropping the very
    // protection this getter exists for.
    const head = html.match(/<head(?=[\s>])[^>]*>/i);
    if (head) return html.replace(head[0], `${head[0]}${meta}`);

    // No <head> of its own, so give the policy one. It must still never precede
    // the doctype — that forces quirks mode and reflows the email, which the
    // old fallback did unconditionally.
    const htmlTag = html.match(/<html(?=[\s>])[^>]*>/i);
    if (htmlTag) return html.replace(htmlTag[0], `${htmlTag[0]}<head>${meta}</head>`);

    const doctype = html.match(/^\s*<!doctype[^>]*>/i);
    if (doctype) return html.replace(doctype[0], `${doctype[0]}<head>${meta}</head>`);

    // A bare fragment: nothing to sit behind, so prepending is safe here.
    return `${meta}${html}`;
  }

  // Distinguishes "still fetching" from "settled with no body" so the dialog
  // shows an unavailable state on a failed/forbidden/empty response instead
  // of a permanent loading message.
  get snapshotLoading() {
    return this.snapshotRow?.hasSentSnapshot ? (this.snapshotData?.isLoading ?? true) : false;
  }

  openSnapshot = (row: NotificationHistoryModel) => {
    this.snapshotRow = row;
  };

  closeSnapshot = () => {
    this.snapshotRow = null;
  };

  previousPage = () => {
    this.page = Math.max(1, this.page - 1);
  };

  nextPage = () => {
    ++this.page;
  };

  changePaginatorPage = (page: number) => {
    this.page = page;
  };

  get totalItems() {
    return this.notificationHistoriesData?.value?.meta?.page?.total ?? 0;
  }

  get totalPages() {
    if (this.totalItems) {
      return Math.ceil(this.totalItems / this.args.limit);
    }
    return 0;
  }

  <template>
    <span {{didIntersect onEnter=(fn (mut this.isInViewport) true)}} />
    {{#if this.isInViewport}}
      <Table @isLoading={{this.isLoading}}>
        <:thead as |options|>
          <options.tr>
            <options.th class="w-48">{{t
                "common.profile.notification_histories.sent_on"
              }}</options.th>
            <options.th>{{t "common.profile.notification_histories.email_type"}}</options.th>
            <options.th>{{t "common.profile.notification_histories.sent_to"}}</options.th>
            <options.th>{{t "common.profile.notification_histories.built_by"}}</options.th>
            <options.th class="w-20"><span class="sr-only">{{t
                  "common.profile.notification_histories.view_sent"
                }}</span></options.th>
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each this.notificationHistories as |history|}}
            <options.tr>
              <options.td>
                {{safeFormatDate history.createdAt month="short" day="2-digit" year="numeric"}}
                {{safeFormatTime
                  history.createdAt
                  hour="numeric"
                  minute="numeric"
                  second="numeric"
                }}
              </options.td>
              <options.td>{{history.noteSubtype}}</options.td>
              <options.td>{{history.toEmail}}</options.td>
              <options.td data-test-built-by={{history.id}}>
                {{! hasSentSnapshot is the provenance signal: the snapshot is
                    only written when tio-api rendered the delivered content. }}
                {{#if history.hasSentSnapshot}}
                  <Chip @size="sm" @intent="success">{{t
                      "common.profile.notification_histories.built_by_tio"
                    }}</Chip>
                {{else}}
                  <Chip @size="sm">{{t
                      "common.profile.notification_histories.built_by_sendgrid"
                    }}</Chip>
                {{/if}}
                {{#if history.renderingMode}}
                  {{! Translate the rendering-mode enum, mirroring mode-chip's
                      shared labels — a raw value would show English to
                      es-mx/fr-ca admins. }}
                  <span class="tio-fg-muted text-xs ml-1" data-test-rendering-mode={{history.id}}>
                    {{t
                      (concat "common.profile.notification_histories.mode_" history.renderingMode)
                    }}
                  </span>
                {{/if}}
              </options.td>
              <options.td>
                <Button
                  @appearance="minimal"
                  @size="sm"
                  data-test-view-sent={{history.id}}
                  {{on "click" (fn this.openSnapshot history)}}
                >{{t "common.profile.notification_histories.view_sent"}}</Button>
              </options.td>
            </options.tr>
          {{else}}
            <options.tr>
              <options.td colspan="5">
                {{t "common.profile.notification_histories.no_communications"}}
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
        <:tfoot>
          <tr>
            <td class="text-right" colspan="9">
              {{#if (gt this.totalPages 1)}}
                <Paginator
                  @simple={{true}}
                  @currentPage={{this.page}}
                  @totalItems={{this.totalItems}}
                  @pageSize={{@limit}}
                  @onChange={{this.changePaginatorPage}}
                />
              {{/if}}
            </td>
          </tr>
        </:tfoot>
      </Table>

      {{#if this.snapshotRow}}
        {{! 2xl, not xl, because the content sets the floor: every TIO email
            lays out at 600px. ModalBody is px-6, so the iframe gets the panel
            width less 48px — an xl panel (36rem) leaves 528px and the email
            scrolls sideways inside it, while 2xl (42rem) leaves 624px. }}
        <Modal
          @isOpen={{true}}
          @onClose={{this.closeSnapshot}}
          @size="2xl"
          @ariaLabel={{t "common.profile.notification_histories.sent_email"}}
        >
          <:header>
            {{#if this.snapshot.sentSubject}}
              {{this.snapshot.sentSubject}}
            {{else}}
              {{this.snapshotRow.noteSubtype}}
            {{/if}}
          </:header>
          <:body>
            {{#if this.snapshotRow.hasSentSnapshot}}
              {{#if this.snapshotLoading}}
                <div class="tio-fg-muted text-sm py-8 text-center" data-test-snapshot-loading>{{t
                    "common.profile.notification_histories.snapshot_loading"
                  }}</div>
              {{else if this.sandboxedSnapshotHtml}}
                {{! The delivered HTML renders fully sandboxed (no scripts,
                    no same-origin, and no network via the injected CSP) — a
                    snapshot, never a re-render: rendering is time-varying by
                    design (render-time footer copy), so only the captured
                    bytes are honest. }}
                {{! The srcdoc email is a full document that paints its own
                    background edge-to-edge, so the frame needs no bg utility
                    of its own (a theme-aware one would wrongly darken an
                    email designed for a light canvas). }}
                <iframe
                  title={{t "common.profile.notification_histories.sent_email"}}
                  sandbox=""
                  srcdoc={{this.sandboxedSnapshotHtml}}
                  class="w-full h-96 rounded border-0"
                  data-test-snapshot-frame
                ></iframe>
                {{! Says why images may be missing — otherwise a blocked remote
                    image reads as a broken email rather than a blocked fetch. }}
                <p class="tio-fg-muted mt-2 text-xs" data-test-snapshot-offline-notice>{{t
                    "common.profile.notification_histories.snapshot_offline_notice"
                  }}</p>
              {{else}}
                {{! Settled with no body — a failed/forbidden request or an
                    empty response, not a still-pending one. }}
                <p class="tio-fg-muted text-sm" data-test-snapshot-unavailable>{{t
                    "common.profile.notification_histories.snapshot_unavailable"
                  }}</p>
              {{/if}}
            {{else}}
              <p class="tio-fg-muted text-sm" data-test-no-snapshot-notice>{{t
                  "common.profile.notification_histories.no_snapshot_notice"
                }}</p>
            {{/if}}
          </:body>
        </Modal>
      {{/if}}
    {{/if}}
  </template>
}
